(() => {
  "use strict";

  const createInitialState = () => ({
    currentStep: 1,

    product: {
      code: "",
      name: "",
      type: "",
      quantity: 1,
      pricePerPerson: null,
      drinkPlanSelected: false,
      drinkPlanPricePerPerson: null,
      minimumDaysBefore: 0,
    },

    reservation: {
      date: "",
      time: "",
    },

    guests: {
      adults: 2,
      children: 0,
    },

    customer: {
      name: "",
      phone: "",
      email: "",
      preorder: "",
      note: "",
    },

    submission: {
      isSubmitting: false,
      clientRequestId: "",
      reservationId: "",
      reservationNumber: "",
      duplicate: false,
      error: "",
    },
  });

  const createUuid = () => {
    if (typeof crypto?.randomUUID === "function") {
      return crypto.randomUUID();
    }

    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;

    const hex = Array.from(bytes, (value) =>
      value.toString(16).padStart(2, "0"),
    ).join("");

    return [
      hex.slice(0, 8),
      hex.slice(8, 12),
      hex.slice(12, 16),
      hex.slice(16, 20),
      hex.slice(20),
    ].join("-");
  };

  class ReservationState {
    constructor() {
      this.data = createInitialState();
      this.listeners = new Set();
    }

    getState() {
      return this.data;
    }

    getSnapshot() {
      return JSON.parse(JSON.stringify(this.data));
    }

    subscribe(listener) {
      if (typeof listener !== "function") return () => {};
      this.listeners.add(listener);
      return () => this.listeners.delete(listener);
    }

    getCustomer() {
      return this.data.customer;
    }

    notify() {
      const snapshot = this.getSnapshot();
      this.listeners.forEach((listener) => {
        try { listener(snapshot); }
        catch (error) { console.error("SRS state listener failed:", error); }
      });
      document.dispatchEvent(new CustomEvent("srs:statechange", { detail: snapshot }));
    }

    invalidateSubmissionRequest() {
      if (this.data.submission.isSubmitting) return;
      this.data.submission.clientRequestId = "";
      this.data.submission.reservationId = "";
      this.data.submission.reservationNumber = "";
      this.data.submission.duplicate = false;
      this.data.submission.error = "";
    }

    getOrCreateClientRequestId() {
      if (!this.data.submission.clientRequestId) {
        this.data.submission.clientRequestId = createUuid();
        this.notify();
      }
      return this.data.submission.clientRequestId;
    }

    setCurrentStep(step) {
      const nextStep = Number(step);
      if (!Number.isInteger(nextStep) || nextStep < 1 || nextStep > 6) return;
      this.data.currentStep = nextStep;
      this.notify();
    }

    setProduct(product = {}) {
      this.invalidateSubmissionRequest();
      this.data.product = {
        ...this.data.product,
        code: String(product.code || ""),
        name: String(product.name || ""),
        type: String(product.type || ""),
        quantity: Math.max(1, Number(product.quantity || 1)),
        pricePerPerson: product.pricePerPerson == null ? null : Number(product.pricePerPerson),
        drinkPlanSelected: Boolean(product.drinkPlanSelected),
        drinkPlanPricePerPerson: product.drinkPlanPricePerPerson == null ? null : Number(product.drinkPlanPricePerPerson),
        minimumDaysBefore: Math.max(0, Number(product.minimumDaysBefore || 0)),
      };
      this.clearAfterProduct(false);
      this.notify();
    }

    setProductQuantity(quantity) {
      this.invalidateSubmissionRequest();
      this.data.product.quantity = Math.max(1, Number(quantity || 1));
      this.notify();
    }

    setDrinkPlanSelected(selected) {
      this.invalidateSubmissionRequest();
      this.data.product.drinkPlanSelected = Boolean(selected);
      this.notify();
    }

    setDate(date) {
      const nextDate = String(date || "");
      if (this.data.reservation.date !== nextDate) {
        this.invalidateSubmissionRequest();
        this.data.reservation.date = nextDate;
        this.data.reservation.time = "";
      }
      this.notify();
    }

    setTime(time) {
      const nextTime = String(time || "");
      if (this.data.reservation.time !== nextTime) {
        this.invalidateSubmissionRequest();
        this.data.reservation.time = nextTime;
      }
      this.notify();
    }

    setGuests({ adults, children } = {}) {
      this.invalidateSubmissionRequest();
      if (adults !== undefined) this.data.guests.adults = Math.max(1, Number(adults || 1));
      if (children !== undefined) this.data.guests.children = Math.max(0, Number(children || 0));
      this.notify();
    }

    setCustomer(customer = {}) {
      this.invalidateSubmissionRequest();
      ["name", "phone", "email", "preorder", "note"].forEach((field) => {
        if (customer[field] !== undefined) this.data.customer[field] = String(customer[field] || "");
      });
      this.notify();
    }

    setSubmission(submission = {}) {
      this.data.submission = { ...this.data.submission, ...submission };
      this.notify();
    }

    clearAfterProduct(shouldNotify = true) {
      this.data.reservation.date = "";
      this.data.reservation.time = "";
      if (shouldNotify) this.notify();
    }

    clearAfterDate() {
      this.invalidateSubmissionRequest();
      this.data.reservation.time = "";
      this.notify();
    }

    reset() {
      this.data = createInitialState();
      this.notify();
    }

    getTotalGuests() {
      return Number(this.data.guests.adults || 0) + Number(this.data.guests.children || 0);
    }

    getRecommendedHotpotQuantity() {
      const totalGuests = this.getTotalGuests();
      return totalGuests <= 0 ? 1 : Math.max(1, Math.ceil(totalGuests / 3));
    }

    isStepComplete(step) {
      switch (Number(step)) {
        case 1: return Boolean(this.data.product.code);
        case 2: return Boolean(this.data.reservation.date && this.data.reservation.time && this.getTotalGuests() >= 1);
        case 3: return Boolean(this.data.reservation.time);
        case 4: return this.getTotalGuests() >= 1;
        case 5: return Boolean(this.data.customer.name.trim() && this.data.customer.phone.trim());
        case 6: return true;
        default: return false;
      }
    }
  }

  window.SRS = window.SRS || {};
  window.SRS.state = new ReservationState();
})();
